/* Feb-2026 · Bonus Add-ons store shelf.
 *
 * Renders every SKU visible to the current user (server pre-filters on
 * `revenue_features` flags) as a card. Buy → Stripe Checkout in a new
 * tab; on return the payments webhook credits the user's account.
 */
import React, { useEffect, useState } from 'react';
import { paymentsApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

async function fetchJson(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) {
    const detail = await r.json().catch(() => ({}));
    throw new Error(detail?.detail || `Request failed (${r.status})`);
  }
  return r.json();
}

export default function BonusAddonsShelf() {
  const [catalog, setCatalog] = useState([]);
  const [mine, setMine]       = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cat, m] = await Promise.all([
        fetchJson('/bonus-addons/catalog'),
        fetchJson('/bonus-addons/mine').catch(() => ({ grants: {} })),
      ]);
      setCatalog(cat?.catalog || []);
      setMine(m?.grants || {});
    } catch (e) {
      toast.error(e.message || 'Failed to load bonus add-ons.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Refresh when a purchase completes in any tab (fired by usePaymentReturn).
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('loom:addon-credited', handler);
    return () => window.removeEventListener('loom:addon-credited', handler);
  }, []);

  const buy = async (row) => {
    setBusy(row.sku);
    try {
      const origin = window.location.origin;
      const r = await fetchJson(`/bonus-addons/${row.sku}/checkout`, {
        method: 'POST',
        body: JSON.stringify({ origin_url: origin }),
      });
      if (r?.url) {
        window.open(r.url, '_blank', 'noopener');
        toast.success('Stripe Checkout opened — complete the payment there.');
      }
    } catch (e) {
      toast.error(e.message || 'Checkout failed.');
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : null;

  const ownedLabel = (sku) => {
    // ── Duration passes ──────────────────────────────────────────────────────
    if (sku === 'priority_queue_30d')    return mine?.priority_queue?.expires_at    ? `until ${fmtDate(mine.priority_queue.expires_at)}`    : null;
    if (sku === 'adobe_canva_pass_30d')  return mine?.adobe_canva_pass?.expires_at  ? `until ${fmtDate(mine.adobe_canva_pass.expires_at)}`  : null;
    if (sku === 'zernio_author_pro_30d') return mine?.zernio_author_pro?.expires_at ? `until ${fmtDate(mine.zernio_author_pro.expires_at)}` : null;
    // ── Consumable credits ───────────────────────────────────────────────────
    if (sku === 'book_launch_kit')           return mine?.launch_kits?.remaining    ? `${mine.launch_kits.remaining} left`        : null;
    if (sku.startsWith('cover_credits_'))    return mine?.cover_credits?.remaining  ? `${mine.cover_credits.remaining} credits`   : null;
    if (sku.startsWith('audiobook_credits_'))return (mine?.audiobook_runs || 0) > 0 ? `${mine.audiobook_runs} run${mine.audiobook_runs !== 1 ? 's' : ''} left` : null;
    // ── Tier-addon à-la-carte credits ────────────────────────────────────────
    if (sku === 'audiobook_narration_1') return (mine?.audiobook_narration || 0) > 0 ? `${mine.audiobook_narration} credit${mine.audiobook_narration !== 1 ? 's' : ''}`                   : null;
    if (sku === 'continuity_audit_1')    return (mine?.continuity_audit    || 0) > 0 ? `${mine.continuity_audit} credit${mine.continuity_audit !== 1 ? 's' : ''}`                          : null;
    if (sku === 'bulk_draft_1')          return (mine?.bulk_draft          || 0) > 0 ? `${mine.bulk_draft} credit${mine.bulk_draft !== 1 ? 's' : ''}`                                      : null;
    if (sku === 'script_conversion_1')   return (mine?.script_conversion   || 0) > 0 ? `${mine.script_conversion} credit${mine.script_conversion !== 1 ? 's' : ''}`                        : null;
    if (sku === 'coaching_session_1')    return (mine?.coaching_session    || 0) > 0 ? `${mine.coaching_session} session${mine.coaching_session !== 1 ? 's' : ''} booked`                  : null;
    if (sku === 'editorial_review_1')    return (mine?.editorial_review    || 0) > 0 ? `${mine.editorial_review} review${mine.editorial_review !== 1 ? 's' : ''} queued`                   : null;
    return null;
  };

  if (loading) {
    return <div className="py-6 text-center text-[12px] text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>;
  }
  if (catalog.length === 0) {
    return (
      <Card data-testid="bonus-addons-empty">
        <CardContent className="py-6 text-center text-[12px] text-muted-foreground">
          No bonus tools have been enabled yet. Your studio admin turns these on as they roll out.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="bonus-addons-shelf">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[13px]">
          <Sparkles className="h-4 w-4 text-primary" /> Bonus Tools
        </CardTitle>
        <p className="text-[11.5px] text-muted-foreground">
          One-time and rolling add-ons you can layer on top of any plan.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {catalog.map((row) => {
            const owned = ownedLabel(row.sku);
            return (
              <div
                key={row.sku}
                className="flex flex-col justify-between rounded-md border border-border p-3"
                data-testid={`bonus-addon-card-${row.sku}`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="text-[12.5px] font-semibold">{row.title}</div>
                    <div className="font-mono-stat text-[13px] text-primary">${row.price_usd.toFixed(2)}</div>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{row.description}</p>
                  {owned && (
                    <p className="mt-2 flex items-center gap-1 text-[11px] text-emerald-500">
                      <Check className="h-3 w-3" /> Active: {owned}
                    </p>
                  )}
                </div>
                <div className="mt-3">
                  <Button
                    size="sm"
                    onClick={() => buy(row)}
                    disabled={busy === row.sku}
                    className="w-full"
                    data-testid={`bonus-addon-buy-${row.sku}`}
                  >
                    {busy === row.sku ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    {' '}Buy · ${row.price_usd.toFixed(2)}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
